import { useCallback } from 'react'

import {
    getCardApr,
    getCardDisplayedProductsForEvent,
    getCurrentWindowUrl,
    getDocumentReferrer,
    getFeatureFlagsForEvent,
    toSnakeCase,
} from '@mediaingenuity/totallymoney.cardloanutils'
import {
    Category,
    trackEvent,
} from '@mediaingenuity/totallymoney.trackingevents'

import {
    CreditCardAlternativeProduct,
    ScoredCreditCardProduct,
} from '@appTypes/EligibilityApi.interface'

import useLogMissingProductClickFields from '@hooks/useLogMissingProductClickFields'

import { getMarketingTrackingData } from '@utils/getMarketingTrackingData'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlags from './useFeatureFlags'

type logResultsScreenDisplayedEventProps =
    | {
          category: 'credit_cards'
          displayedProducts?: ScoredCreditCardProduct[]
          highlightProduct: ScoredCreditCardProduct | null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }
    | {
          category: 'credit_cards_alt'
          displayedProducts?: CreditCardAlternativeProduct[]
          highlightProduct: null
          maxCreditLimit?: ScoredCreditCardProduct['maxCreditLimit']
          numberOfCardsToShow?: number
      }

type LogCreditCardsProductClickBase = {
    category: Category
    clickId: string
    productRankOrder: number | null
    productShownOrder: number | null
}

type LogProductClickEventProps = LogCreditCardsProductClickBase & {
    creditCard: Pick<
        ScoredCreditCardProduct,
        | 'name'
        | 'productCode'
        | 'guaranteedRate'
        | 'maxCreditLimit'
        | 'provider'
        | 'offerDetail'
    >
}

type LogAlternativeProductClickEventProps = {
    altCreditCard: Pick<
        CreditCardAlternativeProduct,
        'name' | 'productCode' | 'apr'
    >
} & LogCreditCardsProductClickBase

const useLogEvents2 = () => {
    const { appQueryState } = useAppQueryState()
    const { send, ids } = useBasin()
    const { featureFlags } = useFeatureFlags()
    const {
        logMissingProductClickFields,
        logMissingResultScreenDisplayedFields,
    } = useLogMissingProductClickFields()

    const trackingData = getMarketingTrackingData(appQueryState)

    const sessionFields = {
        sessionId: ids?.sessionId,
        session_id: ids?.sessionId,
    }

    const logProductClickEvent = useCallback(
        ({
            creditCard,
            category,
            clickId,
            productShownOrder,
            productRankOrder,
        }: LogProductClickEventProps) => {
            logMissingProductClickFields({
                category,
                clickId,
                customerId: appQueryState.customerId,
                productCode: creditCard?.productCode,
                scoreId: appQueryState.scoreId,
            })

            const payload = trackEvent('product_click', {
                ...sessionFields,
                apr: getCardApr({
                    guaranteedRate: creditCard.guaranteedRate,
                    offerDetail: creditCard.offerDetail,
                }),
                lender: creditCard.provider,
                product_code: creditCard.productCode,
                product_name: creditCard.name,
                product_shown_order: productShownOrder,
                product_rank_order: productRankOrder,
                max_credit_limit: creditCard.maxCreditLimit,
                guaranteed_credit_limit:
                    creditCard.guaranteedRate?.guaranteedCreditLimit,

                category,
                click_id: clickId,
                client: 'web',
                customer_id: appQueryState.customerId,
                downsell: appQueryState.referrer === 'loansdownsell',
                gclid: trackingData.gclid,
                origin: getDocumentReferrer(),
                score_id: appQueryState.scoreId!,
                send_id: trackingData.sendId,
                url: getCurrentWindowUrl(),
                utm_campaign_id: appQueryState.campaignId,
                utm_campaign: trackingData.cam,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: trackingData.m,
                utm_term: appQueryState.utmTerm ?? trackingData.k,
                variations: getFeatureFlagsForEvent(featureFlags),
            })

            send('product_click', payload)
        },
        [send, appQueryState, featureFlags, logMissingProductClickFields]
    )

    const logAlternativeProductClickEvent = useCallback(
        ({
            altCreditCard,
            category,
            clickId,
            productShownOrder,
            productRankOrder,
        }: LogAlternativeProductClickEventProps) => {
            logMissingProductClickFields({
                category,
                clickId,
                customerId: appQueryState.customerId,
                productCode: altCreditCard?.productCode,
                scoreId: appQueryState.scoreId,
            })

            const payload = trackEvent('product_click', {
                ...sessionFields,
                apr: altCreditCard.apr,
                lender: altCreditCard.name,
                product_code: altCreditCard.productCode,
                product_name: altCreditCard.name,
                product_shown_order: productShownOrder,
                product_rank_order: productRankOrder,

                category,
                click_id: clickId,
                client: 'web',
                customer_id: appQueryState.customerId,
                downsell: appQueryState.referrer === 'loansdownsell',
                gclid: trackingData.gclid,
                origin: getDocumentReferrer(),
                score_id: appQueryState.scoreId!,
                send_id: trackingData.sendId,
                url: getCurrentWindowUrl(),
                utm_campaign_id: appQueryState.campaignId,
                utm_campaign: trackingData.cam,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: trackingData.m,
                utm_term: appQueryState.utmTerm ?? trackingData.k,
                variations: getFeatureFlagsForEvent(featureFlags),
            })

            send('product_click', payload)
        },
        [send, appQueryState, featureFlags, logMissingProductClickFields]
    )

    const logResultsScreenDisplayedEvent = useCallback(
        ({
            category,
            displayedProducts,
            maxCreditLimit,
            numberOfCardsToShow,
            highlightProduct,
        }: logResultsScreenDisplayedEventProps) => {
            logMissingResultScreenDisplayedFields({
                category,
                customerId: appQueryState.customerId,
                scoreId: appQueryState.scoreId,
            })

            const payload = trackEvent('results_screen_displayed', {
                ...sessionFields,
                url: getCurrentWindowUrl(),
                origin: getDocumentReferrer(),
                variations: getFeatureFlagsForEvent(featureFlags),
                requested_credit_limit: maxCreditLimit,
                send_id: trackingData.sendId,
                category,
                client: 'web',
                filter: appQueryState.category
                    ? toSnakeCase(appQueryState.category!)
                    : undefined,
                order_by: appQueryState.sortBy
                    ? toSnakeCase(appQueryState.sortBy!)
                    : undefined,
                displayed_products: getCardDisplayedProductsForEvent<
                    ScoredCreditCardProduct | CreditCardAlternativeProduct
                >({
                    products: displayedProducts!,
                    numberOfCardsToShow: numberOfCardsToShow,
                    highlightProduct: highlightProduct,
                    category,
                }),
                customer_id: appQueryState.customerId,
                gclid: trackingData.gclid,
                utm_campaign_id: appQueryState.campaignId,
                utm_campaign: trackingData.cam,
                utm_content: appQueryState.utmContent,
                utm_medium: appQueryState.utmMedium,
                utm_source: trackingData.m,
                utm_term: appQueryState.utmTerm ?? trackingData.k,
                score_id: appQueryState.scoreId!,
            })

            send('results_screen_displayed', payload)
        },
        [
            send,
            appQueryState,
            featureFlags,
            logMissingResultScreenDisplayedFields,
        ]
    )

    return {
        logProductClickEvent,
        logAlternativeProductClickEvent,
        logResultsScreenDisplayedEvent,
    }
}

export default useLogEvents2
